import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { IL_COLORS } from "../../../../constants/investmentLeague";

const formatAxisValue = (value) => {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value}`;
};

const PortfolioValueTrend = ({ data = [] }) => {
  const theme = useTheme();
  const t     = theme.custom;

  const yDomain = useMemo(() => {
    if (!data.length) return [0, 100];
    const values = data.map((d) => d.value);
    const min    = Math.min(...values);
    const max    = Math.max(...values);
    const pad    = (max - min) * 0.1 || max * 0.05;
    return [Math.floor(min - pad), Math.ceil(max + pad)];
  }, [data]);

  if (!data.length) return null;

  return (
    <Box
      sx={{
        background:   t.surface,
        border:       `1px solid ${t.border2}`,
        borderRadius: "10px",
        p:            { xs: 2, md: 3 },
        height:       "100%",
      }}
    >
      <Typography sx={{ fontSize: "15px", fontWeight: 600, color: t.text, mb: 2 }}>
        Portfolio Value Trend
      </Typography>

      <Box sx={{ width: "100%", height: { xs: 220, md: 260 } }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 16, right: 16, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="portfolioFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor={IL_COLORS.chart} stopOpacity={0.25} />
                <stop offset="100%" stopColor={IL_COLORS.chart} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={t.border} vertical={false} />
            <XAxis
              dataKey="quarter"
              tick={{ fill: t.text3, fontSize: 12 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={formatAxisValue}
              tick={{ fill: t.text3, fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              domain={yDomain}
            />
            <Tooltip
              formatter={(value) => [
                new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value),
                "Value",
              ]}
              contentStyle={{
                borderRadius: "8px",
                border:       `1px solid ${t.border2}`,
                fontSize:     "12px",
              }}
            />
            <Area
              type="monotone"
              dataKey="value"
              stroke={IL_COLORS.chart}
              strokeWidth={2.5}
              fill="url(#portfolioFill)"
              dot={{ r: 4, fill: IL_COLORS.chart, strokeWidth: 0 }}
              activeDot={{ r: 6 }}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default PortfolioValueTrend;
